import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

// Notification sound using Web Audio API
const createNotificationSound = () => {
  let audioContext = null;
  
  return () => {
    try {
      // Lazy initialization of AudioContext
      if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Resume audio context if suspended (required by browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      // Create a pleasant notification sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant bell-like sound (two-tone chime)
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
      oscillator.frequency.setValueAtTime(1108.73, audioContext.currentTime + 0.1); // C#6 note
      
      oscillator.type = 'sine';
      
      // Quick fade in and out for a soft notification
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  };
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    // Load sound preference from localStorage
    const saved = localStorage.getItem('chatSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const messageListenersRef = useRef(new Map());
  const playSoundRef = useRef(null);
  const lastSoundPlayedRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Initialize notification sound
  useEffect(() => {
    playSoundRef.current = createNotificationSound();
  }, []);

  // Save sound preference
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('chatSoundEnabled', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    const now = Date.now();
    // Prevent sound spam (minimum 1 second between sounds)
    if (now - lastSoundPlayedRef.current < 1000) return;
    
    if (playSoundRef.current) {
      playSoundRef.current();
      lastSoundPlayedRef.current = now;
    }
  }, [soundEnabled]);

  const getWebSocketUrl = useCallback(() => {
    if (!token) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, 'ws');
    return `${wsUrl}/api/ws/chat/${token}`;
  }, [token]);

  const connect = useCallback(() => {
    if (!token || !user?.is_subscribed) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = getWebSocketUrl();
    if (!url) return;

    try {
      const ws = new WebSocket(url);
      
      ws.onopen = () => {
        console.log('🟢 WebSocket connected');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Play notification sound for incoming messages (not from self)
          // Backend sends 'new_message' type for incoming chat messages
          if ((data.type === 'new_message' || data.type === 'message') && data.sender_id !== user?.id) {
            playNotificationSound();
          }
          
          // Notify all registered listeners
          messageListenersRef.current.forEach((callback) => {
            callback(data);
          });
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('🔴 WebSocket closed:', event.code);
        setIsConnected(false);
        wsRef.current = null;

        if (event.code === 4001 || event.code === 4003) {
          setConnectionError('Connexion non autorisée');
          return;
        }

        // Exponential backoff reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`Reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setConnectionError('Impossible de se connecter');
    }
  }, [token, user?.is_subscribed, user?.id, getWebSocketUrl, playNotificationSound]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((recipientId, content) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const messageData = {
        type: 'message',
        recipient_id: recipientId,
        content: content
      };
      wsRef.current.send(JSON.stringify(messageData));
      return true;
    }
    console.warn('WebSocket not connected, cannot send message');
    return false;
  }, []);

  // Subscribe to messages for a specific chat partner
  const subscribeToMessages = useCallback((partnerId, callback) => {
    const key = partnerId;
    messageListenersRef.current.set(key, (data) => {
      // Only call callback if message is from/to this partner
      if (data.sender_id === partnerId || data.recipient_id === partnerId) {
        callback(data);
      }
    });

    return () => {
      messageListenersRef.current.delete(key);
    };
  }, []);

  // Global message listener for notifications
  const onAnyMessage = useCallback((callback) => {
    const key = `global_${Date.now()}`;
    messageListenersRef.current.set(key, callback);
    return () => {
      messageListenersRef.current.delete(key);
    };
  }, []);

  // Connect when user is subscribed
  useEffect(() => {
    if (user?.is_subscribed && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [user?.is_subscribed, token, connect, disconnect]);

  const value = {
    isConnected,
    connectionError,
    sendMessage,
    subscribeToMessages,
    onAnyMessage,
    reconnect: connect,
    soundEnabled,
    toggleSound
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketChat = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketChat must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
